import React, { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import { useToast } from '@wartek-id/toast-v2'
import type { OptionItem } from '@wartek-id/select'

import {
  useAPIDeleteKinerjaHarian,
  useAPIGetKinerjaHarianLogs,
} from 'api/kinerja/kinerja-harian'
import {
  getMonthDateRange,
  KinerjaHarianLogs,
  BannerPengembangan,
} from 'app/KinerjaHarian/components/logs'
import { MONTHS } from 'app/KinerjaHarian/constants'
import { Header } from 'app/KinerjaShared/components/Header'
import { SurveyDialog } from 'app/KinerjaShared/components/SurveyDialog'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import { useKinerjaHarianStore } from 'stores/kinerja-harian'
import { useKinerjaHarianTrackEvent } from 'app/KinerjaHarian/hooks/useKinerjaHarianTrackEvent'
import { capitalizeFirstLetter } from 'utils/text'

import type { SurveyOptionType } from 'app/KinerjaShared/components/SurveyDialog'
import type { KinerjaHarianView } from 'types/kinerja-harian'

export function getDefaultSelectedMonthOption(
  selectedYear: number,
  currentDate = new Date()
) {
  if (selectedYear === currentDate.getFullYear()) {
    const currentMonth = MONTHS[currentDate.getMonth()]

    return `${currentMonth.toLowerCase()}-${selectedYear}`
  }

  return `${MONTHS[0].toLowerCase()}-${selectedYear}`
}

export default function KinerjaHarianLogsPage() {
  const baseUrl = usePengelolaanKinerjaBaseUrl()
  const router = useRouter()
  const toast = useToast()
  const { periodYear } = usePeriodContext()
  const selectedYear = periodYear ?? new Date().getFullYear()
  const { mutate: deleteKinerjaHarian } = useAPIDeleteKinerjaHarian()
  const { trackEvent } = useKinerjaHarianTrackEvent()

  const [selectedMonthOption, setSelectedMonthOption] = useState<string>(() =>
    getDefaultSelectedMonthOption(selectedYear)
  )

  // -----------------------------------------
  // Handle survey dialog
  // -----------------------------------------
  const { showSurveyDialog, setShowSurveyDialog, surveyDone, markSurveyDone } =
    useKinerjaHarianStore()
  const [isSurveyOpen, setIsSurveyOpen] = useState(false)

  const handleSubmitSurvey = () => {
    trackEvent({
      activityType: 'click_popup_survey_kepuasan',
      screenName: 'harian',
      additionalParams: {
        clicked_text: 'Kirim',
      },
    })
  }

  const handleClickSurveyOption = (value: SurveyOptionType) => {
    trackEvent({
      activityType: 'click_popup_survey_kepuasan',
      screenName: 'harian',
      additionalParams: {
        clicked_text: value.toString(),
      },
    })
  }

  // -----------------------------------------
  // Handle guard back navigation for unsaved changes
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: baseUrl,
  })

  const { startDate, endDate } = useMemo(
    () => getMonthDateRange(selectedMonthOption, selectedYear),
    [selectedMonthOption, selectedYear]
  )

  const { data: kinerjaHarianLogsResponse } = useAPIGetKinerjaHarianLogs({
    params: {
      startDate,
      endDate,
    },
    options: {
      useErrorBoundary: false,
    },
  })

  const harianLogs = useMemo(
    () => kinerjaHarianLogsResponse?.data ?? [],
    [kinerjaHarianLogsResponse?.data]
  )

  const handleMonthClick = () => {
    trackEvent({
      activityType: 'click_dropdown_month',
      screenName: 'harian',
      additionalParams: {
        clicked_text: 'month',
      },
    })
  }

  const handleMonthChange = (option: OptionItem) => {
    const isSameMonth = selectedMonthOption === option.option
    setSelectedMonthOption(option.option)

    trackEvent({
      activityType: 'click_dropdown_month',
      screenName: 'harian',
      additionalParams: {
        clicked_text: isSameMonth
          ? 'NULL'
          : capitalizeFirstLetter((option.option ?? '').replace('-', ' ')),
      },
    })
  }

  const handleViewChange = (view: KinerjaHarianView) => {
    trackEvent({
      activityType: 'click_change_view',
      screenName: 'harian',
      additionalParams: {
        clicked_text: view === 'calendar' ? 'Kalender' : 'Daftar',
      },
    })
  }

  useEffect(() => {
    setSelectedMonthOption(getDefaultSelectedMonthOption(selectedYear))
  }, [selectedYear])

  // React to store signal: consume it, open if not done
  useEffect(() => {
    if (!showSurveyDialog) return

    setShowSurveyDialog(false)

    if (!surveyDone) {
      setIsSurveyOpen(true)
    }
  }, [showSurveyDialog, setShowSurveyDialog, surveyDone])

  const handleSurveyClose = (clickedText = 'NULL') => {
    trackEvent({
      activityType: 'click_popup_survey_kepuasan',
      screenName: 'harian',
      additionalParams: {
        clicked_text: clickedText,
      },
    })
    setIsSurveyOpen(false)
    markSurveyDone()
  }

  useEffect(() => {
    if (!router.isReady || !router.query.month) return

    if (router.query.month) {
      const monthOption = getDefaultSelectedMonthOption(
        selectedYear,
        new Date(selectedYear, parseInt(router.query.month as string) - 1, 1)
      )

      setSelectedMonthOption(monthOption)

      // remove month query param after syncing with state to prevent confusion
      router.replace(
        {
          pathname: `${baseUrl}/harian`,
        },
        undefined,
        { shallow: true }
      )
    }
  }, [router, selectedYear, baseUrl])

  React.useEffect(() => {
    trackEvent({
      activityType: 'page_view',
      screenName: 'harian',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <main className="max-w-5xl py-8 mx-auto space-y-8">
      <Header title="Kinerja Harian" onBack={handleBack} />
      <BannerPengembangan />
      <KinerjaHarianLogs
        harianLogs={harianLogs}
        selectedMonthOption={selectedMonthOption}
        selectedYear={selectedYear}
        onCreate={() => {
          trackEvent({
            activityType: 'click_create',
            screenName: 'harian',
          })
          router.push(`${baseUrl}/harian/add`)
        }}
        onDelete={(entryDate) => {
          deleteKinerjaHarian(
            { entryDate, eventType: 'DELETED' },
            {
              onSuccess: () => {
                toast({
                  message: 'Berhasil hapus kinerja!',
                  type: 'success',
                })
              },
              onError: () => {
                toast({
                  message: 'Gagal menghapus Kinerja Harian. Coba lagi.',
                })
              },
            }
          )
        }}
        onEdit={(itemId: string) => {
          trackEvent({
            activityType: 'click_edit',
            screenName: 'harian',
            additionalParams: {
              edit_type: 'edit',
            },
          })
          router.push(`${baseUrl}/harian/edit/${itemId}`)
        }}
        onMonthClick={handleMonthClick}
        onMonthChange={handleMonthChange}
        onViewChange={handleViewChange}
      />

      <SurveyDialog
        autoRedirectSeconds={5}
        formContent={{
          question:
            'Dalam skala 1-5 seberapa mudah Anda melakukan Kinerja Harian?',
          ctaLabel: 'Kirim',
        }}
        formUrl="https://forms.gle/befhiN9NjPU1EvCc7"
        isOpen={isSurveyOpen}
        successContent={{
          title:
            'Ceritakan ke kami lebih lengkap saran dan masukan Anda untuk Kinerja Harian',
          ctaLabel: 'Isi Survei Kinerja Harian',
          ctaCloseLabel: 'x',
          getCountdownText: (secondsRemaining) =>
            `Dalam ${secondsRemaining} detik Anda akan diarahkan ke Survei Kinerja Harian`,
        }}
        onSubmit={handleSubmitSurvey}
        onClose={handleSurveyClose}
        onClickOption={handleClickSurveyOption}
      />
    </main>
  )
}
